import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Copy,
  Trash2,
  Share2,
  MessageSquare,
  Globe,
  GlobeLock,
  Loader2,
} from 'lucide-react';
import { useForms } from '../../hooks/useForms';
import { Button } from '../Button';
import { Reveal } from '../Reveal';
import { EmptyState } from '../ui/EmptyState';
import { Modal } from '../ui/Modal';

export const FormsAllList: React.FC = () => {
  const navigate = useNavigate();
  const { forms = [], loading, deleteForm, duplicateForm } = useForms();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const closeDropdown = () => {
    setActiveDropdown(null);
    setMenuPosition(null);
  };

  const toggleDropdown = (formId: string) => {
    if (activeDropdown === formId) {
      closeDropdown();
      return;
    }
    const trigger = triggerRefs.current[formId];
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const MENU_WIDTH = 192; // w-48
    const GUTTER = 8;
    const left = Math.max(GUTTER, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - GUTTER));
    setMenuPosition({ top: rect.bottom + GUTTER, left });
    setActiveDropdown(formId);
  };

  useEffect(() => {
    if (!activeDropdown) return;
    const onDismiss = () => closeDropdown();
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener('keydown', onKey);
    };
  }, [activeDropdown]);

  const filteredForms = (forms || []).filter(form => {
    const matchesSearch = (form.title || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'published' && form.is_published) ||
      (filterStatus === 'draft' && !form.is_published);
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async () => {
    if (formToDelete) {
      await deleteForm(formToDelete);
      setDeleteModalOpen(false);
      setFormToDelete(null);
    }
  };

  const handleDuplicate = async (formId: string) => {
    await duplicateForm(formId);
    closeDropdown();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <Reveal variant="fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Todos os Formulários</h1>
            <p className="text-neutral-400 mt-1">
              {forms.length} {forms.length === 1 ? 'formulário' : 'formulários'} no total
            </p>
          </div>

          <Button variant="primary" onClick={() => navigate('/dashboard/forms/new')}>
            <Plus className="w-5 h-5 mr-2" />
            Novo Formulário
          </Button>
        </div>
      </Reveal>

      {/* Filters */}
      <Reveal variant="fade-up" delay={100}>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <input
              type="text"
              placeholder="Buscar formulários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="appearance-none w-full bg-neutral-900/50 border border-neutral-700 rounded-xl pl-12 pr-10 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
            >
              <option value="all">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Rascunhos</option>
            </select>
          </div>
        </div>
      </Reveal>

      {/* Forms List */}
      {filteredForms.length === 0 ? (
        <Reveal variant="fade-up" delay={200}>
          <EmptyState
            icon={Search}
            title="Nenhum formulário encontrado"
            description={searchTerm ? 'Tente buscar com outros termos' : 'Crie seu primeiro formulário para começar'}
            action={
              !searchTerm && (
                <Button variant="primary" onClick={() => navigate('/dashboard/forms/new')}>
                  <Plus className="w-5 h-5 mr-2" />
                  Criar Formulário
                </Button>
              )
            }
          />
        </Reveal>
      ) : (
        <div className="space-y-4">
          {filteredForms.map((form, index) => (
            <Reveal key={form.id} variant="fade-up" delay={200 + index * 50}>
              <div className="liquid-glass rounded-2xl p-6 hover:border-neutral-600 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Status Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      form.is_published ? 'bg-green-500/20' : 'bg-neutral-700'
                    }`}>
                      {form.is_published ? (
                        <Globe className="w-5 h-5 text-green-400" />
                      ) : (
                        <GlobeLock className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/forms/${form.id}/edit`)}
                        className="font-semibold text-white truncate text-left hover:text-primary-400 transition-colors focus:outline-none focus-visible:underline min-w-0 block max-w-full"
                        title="Editar formulário"
                      >
                        {form.title}
                      </button>
                      <div className="flex items-center gap-4 mt-1 text-sm text-neutral-500">
                        <span>{form.fields?.length || 0} campos</span>
                        <span>Criado em {formatDate(form.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center flex-shrink-0">
                    <button
                      ref={(el) => { triggerRefs.current[form.id] = el; }}
                      onClick={() => toggleDropdown(form.id)}
                      className="p-3 sm:p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                      title="Mais opções"
                      aria-label="Mais opções"
                      aria-haspopup="menu"
                      aria-expanded={activeDropdown === form.id}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      {/* Dropdown Menu (portal escapes liquid-glass stacking context) */}
      {activeDropdown && menuPosition && (() => {
        const form = filteredForms.find(f => f.id === activeDropdown);
        if (!form) return null;
        return createPortal(
          <>
            <div
              className="fixed inset-0 z-[60]"
              onClick={closeDropdown}
            />
            <div
              role="menu"
              className="fixed w-48 bg-neutral-800 border border-neutral-700 rounded-xl shadow-xl z-[70] py-2"
              style={{ top: menuPosition.top, left: menuPosition.left }}
            >
              {form.is_published && (
                <button
                  role="menuitem"
                  onClick={() => {
                    window.open(`/f/${form.id}`, '_blank');
                    closeDropdown();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
                >
                  <Eye className="w-4 h-4" />
                  Ver formulário
                </button>
              )}
              <button
                role="menuitem"
                onClick={() => {
                  navigate(`/dashboard/forms/${form.id}/responses`);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <MessageSquare className="w-4 h-4" />
                Ver respostas
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  navigate(`/dashboard/forms/${form.id}/edit`);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              {form.is_published && (
                <button
                  role="menuitem"
                  onClick={() => {
                    navigate(`/dashboard/forms/${form.id}/share`);
                    closeDropdown();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
                >
                  <Share2 className="w-4 h-4" />
                  Compartilhar
                </button>
              )}
              <button
                role="menuitem"
                onClick={() => handleDuplicate(form.id)}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Copy className="w-4 h-4" />
                Duplicar
              </button>
              <div className="my-1 border-t border-neutral-700/50" />
              <button
                role="menuitem"
                onClick={() => {
                  setFormToDelete(form.id);
                  setDeleteModalOpen(true);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          </>,
          document.body
        );
      })()}

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFormToDelete(null);
        }}
        title="Excluir formulário"
      >
        <p className="text-neutral-400 mb-6">
          Tem certeza que deseja excluir este formulário? Esta ação não pode ser desfeita e todas as respostas serão perdidas.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            className="!bg-red-500 hover:!bg-red-600"
          >
            Excluir
          </Button>
        </div>
      </Modal>
    </div>
  );
};
